import {useFileQueryContext} from '../../contexts/FileQueryContext'
import {usePath} from '../../contexts/PathContext'
import {useReposAnalytics} from '../../hooks/use-repos-analytics'
import {useUrlCreator} from '../../hooks/use-url-creator'
import {useCurrentRepository} from '@github-ui/current-repository'
import {getScrollableParent} from '@github-ui/get-scrollable-parent'
import {codeNavSearchPath} from '@github-ui/paths'
import {Link} from '@github-ui/react-router'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize, useScreenSize} from '@github-ui/screen-size'
import {useNavigate} from '@github-ui/use-navigate'
import {FocusKeys, scrollIntoView} from '@primer/behaviors'
import {FileDirectoryFillIcon, FileIcon} from '@primer/octicons-react'
import {ActionList, AnchoredOverlay, Flash, Link as PrimerLink, Spinner, useFocusZone} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {positions} from 'fzy.js'
import React, {startTransition, useCallback, useDeferredValue, useEffectEvent, useMemo} from 'react'

import {WebWorker} from '../../utilities/web-worker'
import {findFileWorkerJob, type FindFileRequest, type FindFileResponse} from '../../worker-jobs/find-file'
import {FilesSearchBox, isSearchUrl} from './FilesSearchBox'
import {useTreeList} from '../../hooks/use-tree-list'

import styles from './FileResultsList.module.css'
import {clsx} from 'clsx'

const LIST_SIZE = 40

const defaultConfig = {
  excludeDirectories: false,
  excludeSeeAllResults: false,
}

type FileResultsConfig = {
  excludeDirectories?: boolean
  excludeSeeAllResults?: boolean
  enableOverlay?: boolean
}

type FileResultsListProps = {
  actionListClassName?: string
  additionalResults?: string[]
  commitOid: string
  config?: FileResultsConfig
  findFileWorkerPath: string
  getItemUrl?(path: string, isDirectory: boolean, hash: string): string
  onRenderRow?(): void
  onItemSelected?: (path?: string) => void
  searchBoxRef?: React.RefObject<HTMLInputElement | null>
  className?: string
}

export default function FileResultsList({
  actionListClassName,
  additionalResults,
  commitOid,
  config = defaultConfig,
  findFileWorkerPath,
  getItemUrl,
  onRenderRow,
  onItemSelected,
  searchBoxRef,
  className,
}: FileResultsListProps) {
  const {excludeDirectories, excludeSeeAllResults} = config
  const {query, setQuery} = useFileQueryContext()
  const repo = useCurrentRepository()
  const internalInputRef = React.useRef<HTMLInputElement>(null)
  const inputRef = searchBoxRef ?? internalInputRef
  // Mount results component early to start fetching search paths before user starts typing
  // for a more responsive experience.
  const [preloadSearch, setPreloadSearch] = React.useState(false || query.length > 0)
  const [overlayOpen, setOverlayOpen] = React.useState(!!query)
  const {list, directories, loading, error} = useTreeList(commitOid, preloadSearch, !!excludeDirectories)
  const directoriesSet = useMemo(() => new Set(directories), [directories])
  const path = usePath()
  const {getUrl} = useUrlCreator()
  const {queryText, queryLine} = parseQuery(query)
  const deferredQueryText = useDeferredValue(queryText)
  const combinedList = useMemo(() => {
    if (loading) return undefined
    return [...list, ...(additionalResults ?? [])].sort()
  }, [additionalResults, list, loading])
  const {matches, clearMatches} = useFilter(combinedList, deferredQueryText, findFileWorkerPath, preloadSearch)
  const {sendRepoClickEvent} = useReposAnalytics()
  const navigate = useNavigate()
  const [focusedSearchIndex, setFocusedSearchIndex] = React.useState<number>(0)
  const [listFocusVisible, setListFocusVisible] = React.useState(() => isSearchUrl())
  const allResultsLinkRef = React.useRef<HTMLAnchorElement>(null)
  const textInputContainerRef = React.useRef<HTMLDivElement>(null)
  const overlayId = 'file-results-list'
  const {sendRepoKeyDownEvent} = useReposAnalytics()

  // This is SSR safe because we will wrap calls to FileResultsList with `lazy`
  const {screenSize} = useScreenSize()
  const enableOverlay = config.enableOverlay ?? screenSize >= ScreenSize.large

  const onRowClick = React.useCallback(
    (selectedPath: string) => {
      sendRepoClickEvent('FILE_TREE.SEARCH_RESULT_CLICK')
      onItemSelected?.(selectedPath)
      setOverlayOpen(false)
    },
    [sendRepoClickEvent, onItemSelected],
  )

  const buildUrl = (itemPath: string, knownDirectories: Set<string>, hash: string) => {
    const isDirectory = knownDirectories.has(itemPath)

    if (getItemUrl) return getItemUrl(itemPath, isDirectory, hash)

    return getUrl({
      path: itemPath,
      action: isDirectory ? 'tree' : 'blob',
      hash,
    })
  }

  const {containerRef: listRef} = useFocusZone(
    {
      bindKeys: FocusKeys.ArrowVertical | FocusKeys.HomeAndEnd,
      focusInStrategy: 'previous',
    },
    [loading, error],
  )

  // Close the overlay when the query is cleared. Handled during render rather than in an effect.
  const [prevQuery, setPrevQuery] = React.useState(query)
  if (prevQuery !== query) {
    setPrevQuery(query)
    if (!query) {
      setOverlayOpen(false)
    }
  }

  React.useEffect(() => {
    if (document.activeElement !== inputRef.current && enableOverlay) {
      setOverlayOpen(false)
    }
  }, [path, inputRef, enableOverlay])

  const displayMatches = useMemo(() => matches?.slice(0, LIST_SIZE) ?? [], [matches])

  const matchesTruncated = matches && matches.length > displayMatches.length

  const handleSearchBoxKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const {key, shiftKey, metaKey, altKey, ctrlKey} = event
    if (shiftKey || metaKey || altKey) return

    if (key === 'Escape') {
      if (query) {
        sendRepoKeyDownEvent('FILE_TREE.CANCEL_SEARCH')
        setQuery('')
        clearMatches()
      } else if (document.activeElement) {
        // eslint-disable-next-line github/no-blur
        ;(document.activeElement as HTMLInputElement).blur()
      }
    } else if (!query) {
      return
    } else if (key === 'Enter') {
      if (!excludeSeeAllResults && matchesTruncated && focusedSearchIndex === displayMatches.length) {
        navigate(
          codeNavSearchPath({
            owner: repo.ownerLogin,
            repo: repo.name,
            searchTerm: `path:${queryText}`,
          }),
        )
        onItemSelected?.()
      } else if (displayMatches[focusedSearchIndex]) {
        const selectedPath = displayMatches[focusedSearchIndex]
        const itemUrl = buildUrl(selectedPath, directoriesSet, queryLine ? `L${queryLine}` : '')
        navigate(itemUrl)
        setOverlayOpen(false)
        onItemSelected?.(selectedPath)
      }
    } else if (key === 'ArrowDown' || (ctrlKey && key === 'n')) {
      // Move to the "See all results" link
      if (!excludeSeeAllResults && matchesTruncated && focusedSearchIndex >= displayMatches.length - 1) {
        setFocusedSearchIndex(displayMatches.length)
        if (allResultsLinkRef.current && listRef.current) {
          const container = getScrollableParent(listRef.current)
          scrollIntoView(allResultsLinkRef.current, container as HTMLElement, {behavior: 'instant'})
        }
      } else {
        setFocusedSearchIndex(prev => Math.min(prev + 1, displayMatches.length - 1))
      }

      event.preventDefault()
      return
    } else if (key === 'ArrowUp' || (ctrlKey && key === 'p')) {
      setFocusedSearchIndex(prev => Math.max(prev - 1, 0))
      event.preventDefault()
      return
    }
  }

  const listContents = (
    <div
      className={clsx(styles.Box, {
        [styles.OverlayEnabled]: enableOverlay,
      })}
    >
      {error ? (
        <Flash variant="danger" className="tmp-m-3">
          Failed to search
        </Flash>
      ) : (
        <ActionList
          ref={listRef as React.RefObject<HTMLUListElement | null>}
          className={clsx(actionListClassName, styles.ActionList, {
            [styles.ActionList_Overlay]: enableOverlay,
          })}
          role="listbox"
        >
          {!loading &&
            displayMatches.map((match, index) => {
              const isDirectory = directoriesSet.has(match)
              const itemUrl = buildUrl(match, directoriesSet, queryLine ? `L${queryLine}` : '')
              return (
                <MemoizedFileResultRow
                  active={match === path}
                  index={index}
                  key={match}
                  focused={listFocusVisible && focusedSearchIndex === index}
                  match={match}
                  onRender={onRenderRow}
                  query={deferredQueryText}
                  onClick={onRowClick}
                  isDirectory={isDirectory}
                  to={itemUrl}
                  useOverlay={enableOverlay}
                  listRef={listRef}
                />
              )
            })}

          <div className="tmp-m-3 text-center">
            <FileResultsStatus
              loading={loading || !matches}
              visibleResultCount={displayMatches.length}
              truncated={!!matchesTruncated}
            />
            {matchesTruncated && !excludeSeeAllResults && (
              <>
                &nbsp;
                <PrimerLink
                  id="see-all-results-link"
                  /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                  className={clsx('focus-visible', {
                    [styles.PrimerLink]: listFocusVisible && focusedSearchIndex === displayMatches.length,
                  })}
                  ref={allResultsLinkRef}
                  href={codeNavSearchPath({
                    owner: repo.ownerLogin,
                    repo: repo.name,
                    searchTerm: `path:${queryText}`,
                  })}
                >
                  See all results
                </PrimerLink>
              </>
            )}
          </div>
        </ActionList>
      )}
    </div>
  )

  return (
    <>
      <div ref={textInputContainerRef} className={className}>
        <FilesSearchBox
          ariaActiveDescendant={
            (enableOverlay || !query) && (!enableOverlay || !overlayOpen)
              ? undefined
              : listFocusVisible && focusedSearchIndex > -1
                ? matchesTruncated && focusedSearchIndex === displayMatches.length
                  ? 'see-all-results-link'
                  : `file-result-${focusedSearchIndex}`
                : undefined
          }
          ariaExpanded={enableOverlay ? overlayOpen : undefined}
          ariaHasPopup={enableOverlay}
          ariaControls={enableOverlay ? overlayId : undefined}
          ref={inputRef}
          query={query}
          onKeyDown={handleSearchBoxKeyDown}
          onPreload={() => setPreloadSearch(true)}
          onSearch={newQuery => {
            setQuery(newQuery)
            if (newQuery) {
              setOverlayOpen(true)
            } else {
              clearMatches()
              setOverlayOpen(false)
            }
            setFocusedSearchIndex(0)
          }}
          onBlur={e => {
            if (!listRef.current?.contains(e.relatedTarget)) {
              setOverlayOpen(false)
              setListFocusVisible(false)
            }
          }}
          onFocus={() => {
            if (query) {
              setOverlayOpen(true)
            }
            setListFocusVisible(true)
          }}
          className={styles.FilesSearchBox}
        />
      </div>
      {enableOverlay && (
        <AnchoredOverlay
          anchorRef={textInputContainerRef}
          open={enableOverlay && overlayOpen}
          renderAnchor={null}
          onClose={() => {
            setOverlayOpen(false)
          }}
          focusZoneSettings={{disabled: true}}
          focusTrapSettings={{disabled: true}}
          width="xlarge"
          align="end"
          overlayProps={{id: overlayId, role: 'dialog'}}
        >
          {listContents}
        </AnchoredOverlay>
      )}
      {!enableOverlay && query && listContents}
    </>
  )
}

interface FileResultStatusProps {
  visibleResultCount: number
  truncated: boolean
  loading: boolean
}

function FileResultsStatus({visibleResultCount, truncated, loading}: FileResultStatusProps) {
  const screenReaderOnly = !loading && !truncated && visibleResultCount !== 0
  return (
    <span
      key="results-count-status"
      role="status"
      className={screenReaderOnly ? 'sr-only' : undefined}
      aria-label={loading ? 'Loading' : undefined}
    >
      {loading ? (
        <Spinner size="large" />
      ) : visibleResultCount === 0 ? (
        'No matches found'
      ) : truncated ? (
        `First ${visibleResultCount} files shown.`
      ) : (
        `Showing ${visibleResultCount} files.`
      )}
    </span>
  )
}

interface FileResultRowProps {
  active: boolean
  focused?: boolean
  index: number
  match: string
  isDirectory: boolean
  onClick?(path: string): void
  query: string
  to: string
  onRender?(): void
  useOverlay: boolean
  listRef?: React.RefObject<HTMLElement | null>
}

export const FileResultRow = ({
  active,
  focused,
  index,
  match,
  query,
  to,
  isDirectory,
  onClick,
  onRender,
  useOverlay,
  listRef,
}: FileResultRowProps) => {
  const positionsList = positions(query, match)
  onRender?.()

  const ref = React.useRef<HTMLAnchorElement>(null)
  const leadingIcon = isDirectory ? DirectoryIcon : FileResultIcon

  React.useEffect(() => {
    if (focused && ref.current && listRef?.current) {
      const container = getScrollableParent(listRef.current)
      scrollIntoView(ref.current, container as HTMLElement, {behavior: 'instant'})
    }
  }, [focused, listRef])

  const onClickWrapped = useCallback(() => {
    onClick?.(match)
  }, [match, onClick])

  return (
    <ActionList.Item
      key={match}
      role="group"
      active={active}
      className={clsx(focused ? styles.ActionList_ItemFocused : undefined)}
    >
      <Link
        to={to}
        ref={ref}
        role="option"
        onClick={onClickWrapped}
        id={`file-result-${index}`}
        tabIndex={useOverlay ? -1 : 0}
        data-focus-visible-added={focused || undefined}
        className={clsx('d-flex', styles.ActionList_Item)}
      >
        <div className="d-flex flex-1 flex-column overflow-hidden">
          <HighlightMatch
            text={match}
            positionsList={positionsList}
            LeadingIcon={leadingIcon}
            className={styles.HighlightMatch}
          />
        </div>
        {focused && <div className="sr-only">{`Go to ${isDirectory ? 'folder' : 'file'}`}</div>}
      </Link>
    </ActionList.Item>
  )
}

const DirectoryIcon = () => (
  <Octicon aria-label="Directory" icon={FileDirectoryFillIcon} size="small" className={styles.Octicon} />
)

const FileResultIcon = () => <Octicon aria-label="File" icon={FileIcon} className="fgColor-muted mr-2" size="small" />

const MemoizedFileResultRow = React.memo(FileResultRow)

interface HighlightMatchProps {
  text: string
  positionsList: number[]
  LeadingIcon?: React.ComponentType
  className?: string
}

function HighlightMatch({text, positionsList, className, LeadingIcon}: HighlightMatchProps) {
  const parts = []
  let lastPosition = 0
  for (const i of positionsList) {
    if (Number(i) !== i || i < lastPosition || i > text.length) {
      continue
    }
    const slice = text.slice(lastPosition, i)
    if (slice) {
      parts.push(allowSlashWrapping(slice))
    }

    lastPosition = i + 1

    parts.push(
      <mark key={i} className="text-bold bgColor-transparent fgColor-default">
        {text[i]}
      </mark>,
    )
  }

  parts.push(allowSlashWrapping(text.slice(lastPosition)))

  return (
    <div className={className}>
      <>
        {LeadingIcon && <LeadingIcon />}
        {parts}
      </>
    </div>
  )
}

function allowSlashWrapping(text: string): string {
  // Add a zero-width-space after each slash, so that the browser can wrap the text
  return text.replaceAll('/', '/\u200B')
}

function useFilter(list: string[] | undefined, query: string, workerPath: string, startWorker: boolean) {
  const [matches, setMatches] = React.useState<string[]>()
  const lastQueryRef = React.useRef<string>('')
  const workerRef = React.useRef<WebWorker<FindFileRequest, FindFileResponse> | null>(null)
  const {sendStats} = useReposAnalytics()
  const isWorkerWorkingRef = React.useRef(false)
  // Track the latest query to avoid sending stats for stale results
  const latestQueryRef = React.useRef<string>('')

  const createWorker = useEffectEvent(() => {
    const worker = new WebWorker(workerPath, findFileWorkerJob)

    worker.onmessage = ({data}: {data: FindFileResponse}) => {
      isWorkerWorkingRef.current = false
      startTransition(() => {
        setMatches(data.list)
      })
      lastQueryRef.current = data.query

      // Only send stats if this result is for the latest query (not stale)
      if (data.startTime && data.query === latestQueryRef.current) {
        sendStats('repository.find-file', {
          'find-file-base-count': data.baseCount,
          'find-file-results-count': data.list.length,
          'find-file-duration-ms': performance.now() - data.startTime,
        })
      }
    }

    workerRef.current = worker
  })

  // useEffectEvent allows us to read `matches` without adding it as a dependency.
  // We intentionally filter from previous matches when the query extends (e.g., "a" → "ab")
  // for performance, but we don't want the effect to re-run when matches change.
  const postFilterRequest = useEffectEvent((baseList: string[], currentQuery: string) => {
    const canFilterPreviousMatches = lastQueryRef.current && currentQuery.startsWith(lastQueryRef.current)
    isWorkerWorkingRef.current = true
    latestQueryRef.current = currentQuery
    workerRef.current?.postMessage({
      baseList: (canFilterPreviousMatches && matches) || baseList,
      query: currentQuery,
      startTime: performance.now(),
    })
  })

  React.useEffect(() => {
    if (!startWorker) return
    createWorker()

    return function destroy() {
      workerRef.current?.terminate()
    }
  }, [startWorker])

  React.useEffect(() => {
    if (list === undefined && latestQueryRef.current) {
      // Reset when tree is loading (i.e., list is undefined)
      // after a repo/branch change (latestQueryRef presence indicates this is not the initial mount).
      // Discard stale matches and terminate any in-flight worker.
      isWorkerWorkingRef.current = false
      workerRef.current?.terminate()
      workerRef.current = null
      setMatches(undefined)
    } else if (list?.length && query) {
      // If a worker is currently filtering and we get a new query,
      // don't wait for it to finish. Terminate the worker and start a new one.
      // Also recreate the worker if it was terminated by a reset (see above).
      if (isWorkerWorkingRef.current || !workerRef.current) {
        workerRef.current?.terminate()
        createWorker()
      }
      postFilterRequest(list, query)
    }
  }, [list, query])

  return {
    matches,
    clearMatches: useCallback(() => setMatches(undefined), []),
  }
}

function parseQuery(query: string) {
  query = query.replaceAll(' ', '')
  const colonIndex = query.indexOf(':')
  if (colonIndex >= 0) {
    return {
      queryText: query.substring(0, colonIndex),
      queryLine: parseInt(query.substring(colonIndex + 1), 10),
    }
  }
  return {queryText: query, queryLine: undefined}
}
